import AssistChip from 'LuxKit/Chips/AssistChip'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import NavBarMenuItem from 'components/NavKit/NavBarMenuItem/NavBarMenuItem'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { useActiveStatusMatchCampaigns } from 'loyalty/hooks/useActiveStatusMatchCampaign'
import { LOUNGE_PASSES_PAGE, LOYALTY_CREDIT_CARD_PAGE, LOYALTY_LANDING_PAGE, LOYALTY_OFFERS_HUB_PAGE, LOYALTY_PURCHASE_POINTS_PAGE, LOYALTY_SPEND_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { useLoyaltyCreditCardFeatureFlag } from 'loyalty/hooks/useLoyaltyCreditCardFeatureFlag'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyIsInfiniteAccountTier from 'loyalty/selectors/getLoyaltyIsInfiniteAccountTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { rem } from 'polished'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import SocieteNavBarMenuButton from './SocieteNavBarMenuButton'
import SocieteNavBarMenuDrawer from './SocieteNavBarMenuDrawer'
import { STATUS_MATCH_NAV_CONFIG_BY_REGION, isSocieteStatusMatchNavVisible } from './societeStatusMatchNav'
import useLoyaltyActiveChooseDoubleRewards from 'takeovers/loyaltyChooseDoubleRewardsTakeover/hooks/useLoyaltyActiveChooseDoubleRewards'
import { CHOOSE_DOUBLE_REWARDS_SLUG } from 'takeovers/loyaltyChooseDoubleRewardsTakeover/loyaltyChooseDoubleRewardsTakeover.constants'

const SOCIETE_REWARDS_PATH = '/societe/rewards-seats-maldives'

interface SocieteNavLink {
  label: string
  to?: string
  href?: string
}

interface SocieteNavMenu {
  label: string
  items: Array<SocieteNavLink>
}

const Bar = styled(Sheet)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  padding: 0;

  &.disable-sticky {
    position: relative;
  }
`

const DesktopNavContainer = styled(LayoutContainer)`
  ${mediaQueryUp.desktop} {
    padding: 0 ${rem(20)};
  }
`

const ButtonsContainer = styled.div`
  position: relative;
  display: flex;
  gap: ${rem(4)};
  align-items: center;
  justify-content: flex-start;
`

const DoubleRewardsNavItem = styled(NavBarMenuItem)`
  position: relative;
`

const NewBadge = styled.span`
  position: absolute;
  top: ${rem(-8)};
  inset-inline-end: 0;
  background-color: ${props => props.theme.palette.messaging.critical.normalBackground};
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  padding: ${rem(1)} ${rem(3)};
  border-radius: ${props => props.theme.borderRadius.L};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  font-size: 10px;
  line-height: 13px;

  ${mediaQueryUp.desktop} {
    top: ${rem(2)};
  }
`

const ChipsWrapper = styled.div`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset-inline-end: 0;
    top: 0;
    bottom: 0;
    width: ${rem(48)};
    background: linear-gradient(to left, ${props => props.theme.palette.neutral.surface.normalBackground}, transparent);
    pointer-events: none;
  }

  &:dir(rtl)::after {
    background: linear-gradient(to right, ${props => props.theme.palette.neutral.surface.normalBackground}, transparent);
  }
`

const ChipsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(12)};
  padding-inline-end: ${rem(48)};
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`

function SocieteNavBar() {
  const intl = useIntl()
  const newLabel = intl.formatMessage({
    id: '_2jYquwp3ORw1vKDAJ9x',
    defaultMessage: 'NEW',
    description: 'New badge label for navigation items in the navigation bar',
  })
  const showModal = useContext(ModalContext)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const isInfiniteTier = useAppSelector(getLoyaltyIsInfiniteAccountTier)
  const activeStatusMatchCampaigns = useActiveStatusMatchCampaigns()
  const showDoubleRewards = !!useLoyaltyActiveChooseDoubleRewards()
  const { isEnabled: isCreditCardPageEnabled } = useLoyaltyCreditCardFeatureFlag()
  const barRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(barRef, 'sticky')

  const benefitsMenu = useMemo<SocieteNavMenu>(() => {
    const tierName = getLoyaltyTierDisplayName(tier)
    const programName = getLoyaltyProgramDisplayName()
    let allBenefitsLabel = intl.formatMessage({
      id: 'TzO1hnnPew0LEnzvoM9P',
      defaultMessage: '{programName} Benefits',
      description: 'Dropdown item label for loyalty program benefits in the Societe tertiary navigation menu',
    }, { programName })

    if (isLoggedIn) {
      allBenefitsLabel = intl.formatMessage({
        id: 'Y4lB1Td_gl2-3p3erjHH',
        defaultMessage: 'All {tierName} Benefits',
        description: 'Dropdown item label for all benefits in the Societe tertiary navigation menu',
      }, { tierName })
    }

    return {
      label: intl.formatMessage({
        id: 'gKOCxXyhQq96WU_pxHH0',
        defaultMessage: 'Benefits',
        description: 'Top-level dropdown label for Benefits in the Societe tertiary navigation menu',
      }),
      items: [
        {
          label: allBenefitsLabel,
          // Infinite has no "about" benefits table — send them to the benefits tiles on their membership page.
          to: `/${LOYALTY_LANDING_PAGE}#${isInfiniteTier ? 'benefits-your-tier' : 'about-benefits'}`,
        },
        {
          label: intl.formatMessage({
            id: 'mX61fJmQgKKdEM-KHkQZ',
            defaultMessage: 'Airport Lounges',
            description: 'Dropdown item label for airport lounges in the Societe tertiary navigation menu',
          }),
          to: `/${LOUNGE_PASSES_PAGE}`,
        },
      ],
    }
  }, [intl, isLoggedIn, tier, isInfiniteTier])

  const pointsMenu = useMemo<SocieteNavMenu>(() => ({
    label: intl.formatMessage({
      id: 'yU8mM2OqRw1kfc-BbySW',
      defaultMessage: 'Points',
      description: 'Top-level dropdown label for Points in the Societe tertiary navigation menu',
    }),
    items: [
      ...(currentRegionCode === 'AU' ? [{
        label: intl.formatMessage({
          id: 'ZrnjA-P3SVBGFI_jZRId',
          defaultMessage: 'Société Rewards',
          description: 'Dropdown item label for Societe Rewards in the Societe tertiary navigation menu',
        }),
        to: SOCIETE_REWARDS_PATH,
      }] : []),
      {
        label: intl.formatMessage({
          id: 'OIoiRm70YOrOS-0XaT3C',
          defaultMessage: 'Top up Points',
          description: 'Dropdown item label for buying more points in the Societe tertiary navigation menu',
        }),
        to: `/${LOYALTY_PURCHASE_POINTS_PAGE}`,
      },
      {
        label: intl.formatMessage({
          id: 'DtTAGrnLhr7C-_e8D_T8',
          defaultMessage: 'Use my points',
          description: 'Dropdown item label for spending points in the Societe tertiary navigation menu',
        }),
        to: `/${LOYALTY_SPEND_POINTS_PAGE}`,
      },
    ],
  }), [currentRegionCode, intl])

  const navLinks = useMemo<{
    membershipLink?: SocieteNavLink
    aboutLink?: SocieteNavLink
    creditCardLink?: SocieteNavLink
    offerLink: SocieteNavLink
    statusMatchLink?: SocieteNavLink
    doubleRewardsLink?: SocieteNavLink
  }>(() => {
    const statusMatchConfig = STATUS_MATCH_NAV_CONFIG_BY_REGION[currentRegionCode]
    const showStatusMatchLink = isSocieteStatusMatchNavVisible(statusMatchConfig, activeStatusMatchCampaigns)

    const membershipLink = isLoggedIn ? {
      label: intl.formatMessage({
        id: 'A58hHJjGnrUe-PFDqNKJ',
        defaultMessage: 'Your Membership',
        description: 'Top-level link label in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_LANDING_PAGE}`,
    } : undefined

    // Infinite members have no "About Société" section (its content is gated off for them on the
    // account page), so omit the tertiary-nav link too.
    const aboutLink = isInfiniteTier ? undefined : {
      label: intl.formatMessage({
        id: 'ghD8njE0B2cnhg69mlSq',
        defaultMessage: 'About Société',
        description: 'Top-level link label for the about Société section in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_LANDING_PAGE}#about`,
    }

    const creditCardLink = isCreditCardPageEnabled ? {
      label: intl.formatMessage({
        id: 'NcBO_uAd-G5tqdaLyyEC',
        defaultMessage: 'Credit Card',
        description: 'Top-level link label for the Societe credit card page in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_CREDIT_CARD_PAGE}`,
    } : undefined

    const offerLink = {
      label: intl.formatMessage({
        id: 'WSZ9W_wkwPRyNU4CJ5s-',
        defaultMessage: 'Offers',
        description: 'Top-level link label for the Societe offers hub in the Societe tertiary navigation menu',
      }),
      to: `/${LOYALTY_OFFERS_HUB_PAGE}`,
    }

    const statusMatchLink = statusMatchConfig && showStatusMatchLink ? {
      label: intl.formatMessage({
        id: 'y15_icuBYSRt21kwwSyL',
        defaultMessage: 'Status Match',
        description: 'Top-level link label for Status Match in the Societe tertiary navigation menu',
      }),
      to: statusMatchConfig.path,
    } : undefined

    const doubleRewardsLink = showDoubleRewards ? {
      label: intl.formatMessage({
        id: 'Mb_wNOZN8FBNv-EYzTvT',
        defaultMessage: 'Double Rewards',
        description: 'Top-level link to the double rewards campaign in the Societe tertiary navigation menu',
      }),
      to: `/societe/${CHOOSE_DOUBLE_REWARDS_SLUG}`,
    } : undefined

    return {
      membershipLink,
      aboutLink,
      creditCardLink,
      offerLink,
      statusMatchLink,
      doubleRewardsLink,
    }
  }, [activeStatusMatchCampaigns, showDoubleRewards, currentRegionCode, intl, isLoggedIn, isInfiniteTier, isCreditCardPageEnabled])

  const openBenefitsDrawer = useCallback(() => {
    showModal(<SocieteNavBarMenuDrawer {...benefitsMenu} />)
  }, [benefitsMenu, showModal])

  const openPointsDrawer = useCallback(() => {
    showModal(<SocieteNavBarMenuDrawer {...pointsMenu} />)
  }, [pointsMenu, showModal])

  const {
    membershipLink,
    aboutLink,
    creditCardLink,
    offerLink,
    statusMatchLink,
    doubleRewardsLink,
  } = navLinks

  return <Bar background="normal-background" ref={barRef}>
    <CSSBreakpoint min="desktop">
      <DesktopNavContainer variant="extraLarge">
        <ButtonsContainer>
          {membershipLink && (
            <NavBarMenuItem to={membershipLink.to}>
              {membershipLink.label}
            </NavBarMenuItem>
          )}
          {aboutLink && (
            <NavBarMenuItem to={aboutLink.to}>
              {aboutLink.label}
            </NavBarMenuItem>
          )}
          <SocieteNavBarMenuButton {...benefitsMenu} />
          <SocieteNavBarMenuButton {...pointsMenu} />
          <NavBarMenuItem to={offerLink.to} href={offerLink.href}>
            {offerLink.label}
          </NavBarMenuItem>
          {statusMatchLink && (
            <NavBarMenuItem to={statusMatchLink.to} href={statusMatchLink.href}>
              {statusMatchLink.label}
            </NavBarMenuItem>
          )}
          {creditCardLink && (
            <NavBarMenuItem to={creditCardLink.to}>
              {creditCardLink.label}
            </NavBarMenuItem>
          )}
          {doubleRewardsLink && (
            <DoubleRewardsNavItem to={doubleRewardsLink.to}>
              {doubleRewardsLink.label}
              <NewBadge>{newLabel}</NewBadge>
            </DoubleRewardsNavItem>
          )}
        </ButtonsContainer>
      </DesktopNavContainer>
    </CSSBreakpoint>

    <CSSBreakpoint max="tablet">
      <ChipsWrapper>
        <ChipsContainer>
          {membershipLink && <AssistChip
            variant="filled"
            size="small"
            to={membershipLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {membershipLink.label}
            </Group>
          </AssistChip>}
          {aboutLink && <AssistChip
            variant="filled"
            size="small"
            to={aboutLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {aboutLink.label}
            </Group>
          </AssistChip>}
          <AssistChip
            variant="filled"
            size="small"
            onClick={openBenefitsDrawer}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {benefitsMenu.label}
              <LineAngleDownIcon size={16} colour="normal-contrast" />
            </Group>
          </AssistChip>
          <AssistChip
            variant="filled"
            size="small"
            onClick={openPointsDrawer}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {pointsMenu.label}
              <LineAngleDownIcon size={16} colour="normal-contrast" />
            </Group>
          </AssistChip>
          <AssistChip
            variant="filled"
            size="small"
            to={offerLink.to}
            href={offerLink.href}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {offerLink.label}
            </Group>
          </AssistChip>
          {statusMatchLink && <AssistChip
            variant="filled"
            size="small"
            to={statusMatchLink.to}
            href={statusMatchLink.href}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {statusMatchLink.label}
            </Group>
          </AssistChip>}
          {creditCardLink && <AssistChip
            variant="filled"
            size="small"
            to={creditCardLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {creditCardLink.label}
            </Group>
          </AssistChip>}
          {doubleRewardsLink && <AssistChip
            variant="filled"
            size="small"
            to={doubleRewardsLink.to}
          >
            <Group direction="horizontal" gap={4} verticalAlign="center">
              {doubleRewardsLink.label}
              <NewBadge>{newLabel}</NewBadge>
            </Group>
          </AssistChip>}
        </ChipsContainer>
      </ChipsWrapper>
    </CSSBreakpoint>
  </Bar>
}

export default SocieteNavBar
